/* © CNES */
import { getWidth } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, get, Projection } from 'ol/proj';
import { get as getProjection } from 'ol/proj.js';
import TileWMS, { Options } from 'ol/source/TileWMS';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import TileGrid from 'ol/tilegrid/TileGrid';
import { Dialog } from 'primereact/dialog';
import { useContext, useEffect, useState } from 'react';
import { apiConfiguration } from '../../../api/APIConfiguration';
import { allVariableOptions } from '../../../assets/config/filters/PostelReferenciel';
import { refGeoserverLayers } from '../../../assets/config/filters/Referenciel';
import { ConfigContext } from '../../../context/ConfigContext';
import { FilterDataContext } from '../../../context/FilterDataContext';
import { Feature } from '../../../models/Feature';
import { MapDetails } from '../../../models/MapDetails';
import { TileSourceInfo } from '../../../models/TileSourceInfo';
import {
  ChangeTileControl,
  Controls,
  DrawShapeControl,
  FullScreenControl,
  ZoomControl,
} from '../../map/controls';
import { Layers, TileLayer, VectorLayer } from '../../map/layers';
import Map from '../../map/Map';
import { osm, vector, xyz } from '../../map/sources';
import {
  CLOUD_COVER,
  DATASET,
  EO_SNOW_COVER,
  GRID_CODE,
  IDENTIFIER,
  INSTRUMENT_MODE,
  NODATA_PIXEL,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
  PLATFORM,
  PROCESSING_LEVEL,
  PRODUCT_ACQUISITION_TYPE,
  VISIBILITY,
  WATER_COVER,
} from '../../shared/Constants';
import {
  formatGenericCoverParameter,
  formatTimeParameter,
  generateTileSource,
  tileSourceReference,
} from '../../shared/map/MapUtils';
import './MapContainer.scss';
import { MapViewContainer } from './MapViewContainer/MapViewContainer';
import {
  formatCQLFilterWithCO3D,
  formatCQLFilterWithCriteria,
  formatCQLFilterWithPostel,
  formatCQLFilterWithProductNames,
  formatCQLFilterWithS1Criteria,
  formatCQLFilterWithSpirit,
  formatCQLFilterWithTrishna,
  prepareMapViewContainerInputs,
} from './Utils';

/**
 * CQLFilter Type
 */
type CQLFilterType = {
  filter: string;
  isDisplayed: boolean;
};

/**
 * styles
 */
let styles = {
  MultiPolygon: new Style({
    stroke: new Stroke({
      color: 'transparent',
      width: 3,
    }),
    fill: new Fill({
      color: 'transparent',
    }),
  }),
  SinglePolygon: new Style({
    stroke: new Stroke({
      color: '#ffa500',
      width: 3,
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)',
    }),
  }),
};

/**
 * MapContainer displays Map on screen.
 * @param isFiltersVisible is Filters View visible on UI.
 * @param maxFeatures maximum features to show on map.
 * @returns MapContainer Element.
 */
const MapContainer = ({
  isFiltersVisible,
  maxFeatures,
}: {
  isFiltersVisible: boolean;
  maxFeatures: number;
}) => {
  const [center] = useState([1.43333, 43.6]);
  const [isInDrawMode, setIsInDrawMode] = useState(false);
  const [isInChangeTileMode, setChangeTileMode] = useState(false);
  const [source, setSource] = useState(osm());
  const context = useContext(ConfigContext);
  const { filterData, selectedMissions } = useContext(FilterDataContext);
  const [zoom] = useState<number>(0);
  const [CQLFilterS1, setCQLFilterS1] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterSentinel2, setCQLFilterSentinel2] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterS2L1C, setCQLFilterS2L1C] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterSpot_reflectance, setCQLFilterSpot_reflectance] =
    useState<CQLFilterType>({
      filter: '',
      isDisplayed: false,
    });
  const [CQLFilterSpirit, setCQLFilterSpirit] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterVenus, setCQLFilterVenus] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterOSO, setCQLFilterOSO] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterLandSat57, setCQLFilterLandSat57] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterLandSat8, setCQLFilterLandSat8] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterGdhSnow, setCQLFilterGdhSnow] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterPostel, setCQLFilterPostel] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterTrishna, setCQLFilterTrishna] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [CQLFilterCO3D, setCQLFilterCO3D] = useState<CQLFilterType>({
    filter: '',
    isDisplayed: false,
  });
  const [tileSourceTable, setTileSourceTable] = useState<
    { tile: TileWMS; showLayer: boolean }[]
  >([] as { tile: TileWMS; showLayer: boolean }[]);
  const [bbox, setBbox] = useState<string | undefined>(undefined);
  const projExtent = getProjection('EPSG:3857')?.getExtent();
  const startResolution = projExtent ? getWidth(projExtent) / 256 : 0;
  const resolutions = new Array(22);
  for (let i = 0, ii = resolutions.length; i < ii; ++i) {
    resolutions[i] = startResolution / Math.pow(2, i);
  }
  const [mapViewContainerInputs, setMapViewContainerInputs] = useState<
    MapDetails[] | null
  >(null);
  const [selectedFeature, setSelectedFeature] = useState<Feature | undefined>(
    undefined
  );

  const [tileGrid, setTileGrid] = useState(
    new TileGrid({
      extent: context.mapExtent?.extent,
      resolutions: resolutions,
    })
  );

  useEffect(() => {
    const CQL_Filters = [
      {
        name: 'gdh_s1',
        CQLFilter: CQLFilterS1.filter,
        showLayer: !!CQLFilterS1.isDisplayed,
      },
      {
        name: 'muscate:gdh_s2_l2a3a',
        CQLFilter: CQLFilterSentinel2.filter,
        showLayer: !!CQLFilterSentinel2.isDisplayed,
      },
      {
        name: 'muscate:gdh_l2bl3b',
        CQLFilter: CQLFilterGdhSnow.filter,
        showLayer: !!CQLFilterGdhSnow.isDisplayed,
      },
      {
        name: 'peps:gdh_s2_l1c',
        CQLFilter: CQLFilterS2L1C.filter,
        showLayer: !!CQLFilterS2L1C.isDisplayed,
      },
      {
        name: 'histo:gdh_spot',
        CQLFilter: CQLFilterSpot_reflectance.filter,
        showLayer: !!CQLFilterSpot_reflectance.isDisplayed,
      },
      {
        name: 'muscate:gdh_venus',
        CQLFilter: CQLFilterVenus.filter,
        showLayer: !!CQLFilterVenus.isDisplayed,
      },
      {
        name: 'histo:gdh_spotdem',
        CQLFilter: CQLFilterSpirit.filter,
        showLayer: !!CQLFilterSpirit.isDisplayed,
      },
      {
        name: 'histo:gdh_landsat57',
        CQLFilter: CQLFilterLandSat57.filter,
        showLayer: CQLFilterLandSat57.isDisplayed,
      },
      {
        name: 'muscate:gdh_landsat8',
        CQLFilter: CQLFilterLandSat8.filter,
        showLayer: CQLFilterLandSat8.isDisplayed,
      },
      {
        name: 'muscate:gdh_oso',
        CQLFilter: CQLFilterOSO.filter,
        showLayer: CQLFilterOSO.isDisplayed,
      },
      {
        name: 'histo:gdh_postel',
        CQLFilter: CQLFilterPostel.filter,
        showLayer: CQLFilterPostel.isDisplayed,
      },
      {
        name: 'trishna:gdh_trishna',
        CQLFilter: CQLFilterTrishna.filter,
        showLayer: CQLFilterTrishna.isDisplayed,
      },
      {
        name: 'co3d:gdh_co3d',
        CQLFilter: CQLFilterCO3D.filter,
        showLayer: CQLFilterCO3D.isDisplayed,
      },
    ];

    const tileSourceTable = generateTileSource(
      apiConfiguration.apiBaseUrl,
      formatTimeParameter(
        isFiltersVisible,
        filterData,
        context.searchedFeatures
      ),
      CQL_Filters,
      formatGenericCoverParameter(filterData[CLOUD_COVER]),
      formatGenericCoverParameter(filterData[EO_SNOW_COVER]),
      formatGenericCoverParameter(filterData[WATER_COVER]),
      formatGenericCoverParameter(filterData[NODATA_PIXEL]),
      bbox,
      tileGrid
    ).map((tileSourceProps: TileSourceInfo) => {
      return {
        tile: new TileWMS(tileSourceProps as Options | undefined),
        showLayer:
          CQL_Filters.find(
            (CQL_Filter) => CQL_Filter.name === tileSourceProps.params.LAYERS
          )?.showLayer ?? false,
      };
    });
    setTileSourceTable(tileSourceTable);
  }, [
    bbox,
    context.searchedFeatures,
    filterData,
    isFiltersVisible,
    tileGrid,
    CQLFilterSpot_reflectance,
    CQLFilterS1,
    CQLFilterSentinel2,
    CQLFilterVenus,
    CQLFilterOSO,
    CQLFilterLandSat57,
    CQLFilterLandSat8,
    CQLFilterGdhSnow,
    CQLFilterPostel,
    CQLFilterS2L1C,
    CQLFilterSpirit,
    CQLFilterTrishna,
    CQLFilterCO3D,
  ]);

  useEffect(() => {
    setSelectedFeature(
      context.searchedFeatures?.find(
        (feature: Feature) =>
          context.selectedProduct.productName === feature.properties[IDENTIFIER]
      )
    );
  }, [context.selectedProduct, context.searchedFeatures]);

  /**
   * change Source To OSM on click.
   */
  const changeSourceToOSM = () => {
    // change tile
    setSource(osm());
    setChangeTileMode(false);
  };

  /**
   * change Source on click.
   */
  const changeSource = (attributions: string, url: string) => {
    // change tile
    setSource(
      xyz({
        attributions,
        url,
        maxZoom: 10,
      })
    );
    setChangeTileMode(false);
  };

  /**
   * handle show layer of S1 collection and format CQL filter depending on filter or results view
   * @param searchedFeaturesNames searched feature names.
   */
  const handleSetCQLFilterForS1 = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForS1 = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'peps:gdh_s1')
          ?.dataTypes.includes(feature.collection)
    );
    const isLayerShown = showLayer('peps:gdh_s1');

    if (
      isLayerShown &&
      searchedFeaturesNamesForS1?.length &&
      !isFiltersVisible
    ) {
      setCQLFilterS1({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForS1.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      context.featuresCount < maxFeatures &&
      context.featuresCount > 0
    ) {
      const formattedCQLFilterS1 = formatCQLFilterWithS1Criteria(
        filterData,
        context
      );
      setCQLFilterS1({ filter: formattedCQLFilterS1, isDisplayed: true });
    } else {
      setCQLFilterS1({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer of S2 collection and L1C level and format CQL filter depending on filter or results view
   * @param searchedFeaturesNames searched feature names.
   */
  const handleSetCQLFilterForS2L1C = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForS2L1C = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'peps:gdh_s2_l1c')
          ?.dataTypes.includes(feature.collection)
    );
    const isLayerShown = showLayer('peps:gdh_s2_l1c');
    if (
      isLayerShown &&
      searchedFeaturesNamesForS2L1C?.length &&
      !isFiltersVisible
    ) {
      setCQLFilterS2L1C({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForS2L1C.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      (!filterData[PROCESSING_LEVEL]?.length ||
        filterData[PROCESSING_LEVEL]?.includes('L1C')) &&
      context.featuresCount < maxFeatures &&
      context.featuresCount > 0
    ) {
      const formattedCQLFilterS2L1C = formatCQLFilterWithCriteria(
        filterData,
        context,
        ['S2A', 'S2B', 'S2C']
      );
      setCQLFilterS2L1C({ filter: formattedCQLFilterS2L1C, isDisplayed: true });
    } else {
      setCQLFilterS2L1C({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer of S2 collection ALL BUT L1C level and format CQL filter depending on filter or results view
   * @param searchedFeaturesNames searched feature names.
   */
  const handleSetCQLFilterForS2 = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForS2 = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'muscate:gdh_s2_l2a3a')
          ?.dataTypes.includes(feature.collection)
    );
    const isLayerShown = showLayer('muscate:gdh_s2_l2a3a');

    if (
      isLayerShown &&
      searchedFeaturesNamesForS2?.length &&
      !isFiltersVisible
    ) {
      setCQLFilterSentinel2({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForS2.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      (!filterData[PROCESSING_LEVEL]?.length ||
        filterData[PROCESSING_LEVEL]?.includes('L2A') ||
        filterData[PROCESSING_LEVEL]?.includes('L3A')) &&
      context.featuresCount < maxFeatures &&
      context.featuresCount > 0
    ) {
      const formattedCQLFilterS2 = formatCQLFilterWithCriteria(
        filterData,
        context,
        ['S2A', 'S2B', 'S2C', 'S2X'],
        ['L2A', 'L3A']
      );
      setCQLFilterSentinel2({
        filter: formattedCQLFilterS2,
        isDisplayed: true,
      });
    } else {
      setCQLFilterSentinel2({ filter: '', isDisplayed: false });
    }
  };

  /**
   * LandSat Effect to show layer and format CQL filter depending on results.
   * @param searchedFeaturesNames searched feature names.
   */
  const handleSetCQLFilterForLandsat57 = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForLandsat = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'histo:gdh_landsat57')
          ?.dataTypes.includes(feature.collection)
    );
    const isLayerShown = showLayer('histo:gdh_landsat57');

    if (
      isLayerShown &&
      searchedFeaturesNamesForLandsat.length &&
      !isFiltersVisible
    ) {
      setCQLFilterLandSat57({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForLandsat.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      (!filterData[PLATFORM]?.length ||
        filterData[PLATFORM]?.includes('LANDSAT5') ||
        filterData[PLATFORM]?.includes('LANDSAT7')) &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const formattedCQLFilterLandsat = formatCQLFilterWithCriteria(
        filterData,
        context,
        ['LANDSAT5', 'LANDSAT7']
      );

      setCQLFilterLandSat57({
        filter: formattedCQLFilterLandsat,
        isDisplayed: true,
      });
    } else {
      setCQLFilterLandSat57({ filter: '', isDisplayed: false });
    }
  };

  /**
   * LandSat Effect to show layer and format CQL filter depending on results.
   * @param searchedFeaturesNames searched feature names.
   */
  const handleSetCQLFilterForLandsat8 = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesLandsat8 = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'muscate:gdh_landsat8')
          ?.dataTypes.includes(feature.collection)
    );
    const isLayerShown = showLayer('muscate:gdh_landsat8');

    if (
      isLayerShown &&
      searchedFeaturesNamesLandsat8.length &&
      !isFiltersVisible
    ) {
      setCQLFilterLandSat8({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesLandsat8.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      (!filterData[PLATFORM]?.length ||
        filterData[PLATFORM]?.includes('LANDSAT8')) &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures &&
      !(
        filterData[PROCESSING_LEVEL]?.length === 1 &&
        filterData[PROCESSING_LEVEL][0] === 'L2B-SNOW'
      ) // if L2B-SNOW alone is selected, do not show Landsat8 products in filters view.
    ) {
      const formattedCQLFilterLandsat = formatCQLFilterWithCriteria(
        filterData,
        context
      );

      setCQLFilterLandSat8({
        filter: formattedCQLFilterLandsat,
        isDisplayed: true,
      });
    } else {
      setCQLFilterLandSat8({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer of OSO collection and format CQL filter depending on filter or results view
   * @param searchedFeaturesNames list of features searched
   */
  const handleSetCQLFilterForOSO = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForOSO = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'muscate:gdh_oso')
          ?.dataTypes.includes(feature.collection)
    );
    const isLayerShown = showLayer('muscate:gdh_oso');

    if (
      isLayerShown &&
      searchedFeaturesNamesForOSO.length &&
      !isFiltersVisible
    ) {
      setCQLFilterOSO({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForOSO.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const formattedCQLFilterOSO = formatCQLFilterWithCriteria(
        filterData,
        context
      );
      setCQLFilterOSO({
        filter: formattedCQLFilterOSO,
        isDisplayed: true,
      });
    } else {
      setCQLFilterOSO({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer of Gdh snow through multiple collections and format CQL filter depending on filter or results view
   * @param searchedFeaturesNames list of features searched
   */
  const handleSetCQLFilterForGdhSnow = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForGdhSnow = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'muscate:gdh_l2bl3b')
          ?.dataTypes.includes(feature.collection)
    );
    const snowDatasets = (filterData[DATASET] as string[])?.filter(
      (name: string) =>
        name.includes('L2B-SNOW') ||
        name.includes('L3B') ||
        name.includes('L2B-WATER') ||
        name.includes('BIOPHY')
    );

    const snowDataProducts = (filterData['dataProduct'] as string[])
      ?.filter(
        (name: string) =>
          name.includes('L2B-SNOW') ||
          name.includes('L3B') ||
          name.includes('L2B-WATER') ||
          name.includes('BIOPHY')
      )
      .map((dataProduct) => {
        // remove L2B_ from dataProduct because dataset name does not follow nomenclature.
        return dataProduct.replace('L2B_', '');
      });

    const isLayerShown = showLayer('muscate:gdh_l2bl3b');

    if (
      isLayerShown &&
      searchedFeaturesNamesForGdhSnow.length &&
      !isFiltersVisible
    ) {
      setCQLFilterGdhSnow({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForGdhSnow.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const CQLSnowStringCondition = filterData['dataProduct']?.length
        ? snowDatasets.filter((dataset) =>
            snowDataProducts.find((dataProduct) =>
              dataset.includes(dataProduct)
            )
          )
        : snowDatasets;

      // Determine which satellites are actually selected by the user based on selected missions
      // Mission IDs: Sentinel-1=2, Sentinel-2=3, Venus=4, Landsat=6, Multisat=7
      const selectedSnowSatellites = new Set<string>();

      if (selectedMissions && Array.isArray(selectedMissions)) {
        // Check if Sentinel-2 is selected (mission ID = 3)
        if (selectedMissions.includes(3)) {
          selectedSnowSatellites.add('SENTINEL2');
        }

        // Check if Multisat is selected (mission ID = 7)
        if (selectedMissions.includes(7)) {
          selectedSnowSatellites.add('MULTISAT');
        }

        // Check if Landsat is selected (mission ID = 6)
        if (selectedMissions.includes(6)) {
          selectedSnowSatellites.add('LANDSAT8');
        }
      }

      // Generate CQL string only for selected satellites
      const CQLSnowString = CQLSnowStringCondition.length && selectedSnowSatellites.size > 0
        ? Array.from(selectedSnowSatellites)
            .map((satellite) => `name like '%${satellite}%'`)
            .join(' or ')
        : '';

      const onePlatformAtLeastHasSnow = filterData[PLATFORM]
        ? (filterData[PLATFORM] as string[])?.find((filterPlat) =>
            ['S2A', 'S2B', 'S2C', 'MULTISAT', 'LANDSAT8'].includes(filterPlat)
          )
        : true;

      if (CQLSnowString && onePlatformAtLeastHasSnow) {
        const CQLWithCriteria = formatCQLFilterWithCriteria(
          filterData,
          context,
          ['S2A', 'S2B', 'S2C', 'MULTISAT', 'LANDSAT8'],
          ['L2B-SNOW', 'L3B', 'L2B-WATER', 'L2B', 'L2B_BIOPHY'],
          'muscate:gdh_l2bl3b' // Nom de la couche pour appliquer le filtre productType spécifique
        );
        const cqlFilter = CQLWithCriteria.length
          ? `${CQLSnowString} and ${CQLWithCriteria}`
          : CQLSnowString;
        setCQLFilterGdhSnow({
          filter: cqlFilter,
          isDisplayed: true,
        });
      } else {
        setCQLFilterGdhSnow({ filter: '', isDisplayed: false });
      }
    } else {
      setCQLFilterGdhSnow({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer and format CQL filter of a similar list of collection depending on filter or results view
   * @param searchedFeaturesNames list of features searched
   * @param collections list of similar collection
   */
  const handleSetCQLFilterForMultipleCollections = (
    searchedFeaturesSummary: { name: string; collection: string }[],
    collections: {
      layer: string;
      allSatellitePlatform?: string[];
      allProductLevel?: string[];
      setCQLState: React.Dispatch<React.SetStateAction<CQLFilterType>>;
    }[]
  ) => {
    collections.forEach((collection) => {
      const productNamesResults = searchedFeaturesSummary.filter(
        (feature: { name: string; collection: string }) =>
          !!refGeoserverLayers
            .find((refLayer) => refLayer.layer === collection.layer)
            ?.dataTypes.includes(feature.collection)
      );
      const isLayerShown = showLayer(collection.layer);
      if (isLayerShown && productNamesResults.length && !isFiltersVisible) {
        collection.setCQLState({
          filter: formatCQLFilterWithProductNames(
            productNamesResults.map((feature) => feature.name)
          ),
          isDisplayed: true,
        });
      } else if (
        isLayerShown &&
        isFiltersVisible &&
        context.featuresCount > 0 &&
        context.featuresCount < maxFeatures
      ) {
        const formattedCQLFilter = formatCQLFilterWithCriteria(
          filterData,
          context,
          collection.allSatellitePlatform,
          collection.allProductLevel
        );
        collection.setCQLState({
          filter: formattedCQLFilter,
          isDisplayed: true,
        });
      } else {
        collection.setCQLState({ filter: '', isDisplayed: false });
      }
    });
  };

  /**
   * handle show layer and format CQL filter of a similar list of collection depending on filter or results view
   * @param searchedFeaturesNames list of features searched
   * @param collections list of similar collection
   */
  const handleSetCQLFilterForSpirit = (
    searchedFeaturesSummary: { name: string; collection: string }[],
    collection: {
      allSatellitePlatform?: string[];
      allProductLevel?: string[];
      allProductType?: string[];
      setCQLState: React.Dispatch<React.SetStateAction<CQLFilterType>>;
    }
  ) => {
    const productNamesResults = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'histo:gdh_spotdem')
          ?.dataTypes.includes(feature.collection)
    );

    const isLayerShown = showLayer('histo:gdh_spotdem');

    if (isLayerShown && productNamesResults.length && !isFiltersVisible) {
      collection.setCQLState({
        filter: formatCQLFilterWithProductNames(
          productNamesResults
            .map((feature) => feature.name)
            .filter((name) => name.startsWith('GES') || name.startsWith('SPI'))
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      (!filterData[PROCESSING_LEVEL]?.length ||
        filterData[PROCESSING_LEVEL]?.includes('L1A')) &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const formattedCQLFilter = formatCQLFilterWithSpirit(filterData, context);

      collection.setCQLState({ filter: formattedCQLFilter, isDisplayed: true });
    } else {
      collection.setCQLState({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer of Postel collection and format CQL filter depending on filter or results view
   * @param searchedFeaturesNames list of features searched
   */
  const handleSetCQLFilterForPostel = (
    searchedFeaturesSummary: { name: string; collection: string }[]
  ) => {
    const searchedFeaturesNamesForPostel = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'histo:gdh_postel')
          ?.dataTypes.includes(feature.collection)
    );

    const isLayerShown = showLayer('histo:gdh_postel');

    if (
      isLayerShown &&
      searchedFeaturesNamesForPostel?.length &&
      !isFiltersVisible
    ) {
      setCQLFilterPostel({
        filter: formatCQLFilterWithProductNames(
          searchedFeaturesNamesForPostel.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const formattedCQLFilterPostel = formatCQLFilterWithPostel(
        filterData,
        context,
        ['Vegetation', 'Radiation', 'Water', 'LandCover'],
        allVariableOptions.map((option) => option.key)
      );
      setCQLFilterPostel({
        filter: formattedCQLFilterPostel,
        isDisplayed: true,
      });
    } else {
      setCQLFilterPostel({ filter: '', isDisplayed: false });
    }
  };

  /**
   * handle show layer and format CQL filter of a similar list of collection depending on filter or results view
   * @param searchedFeaturesSummary
   * @param collection
   */
  const handleSetCQLFilterForTrishna = (
    searchedFeaturesSummary: { name: string; collection: string }[],
    collection: {
      allProductLevel?: string[];
      allProductType?: string[];
      setCQLState: React.Dispatch<React.SetStateAction<CQLFilterType>>;
    }
  ) => {
    const productNamesResults = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'trishna:gdh_trishna')
          ?.dataTypes.includes(feature.collection)
    );

    const isLayerShown = showLayer('trishna:gdh_trishna');

    if (isLayerShown && productNamesResults.length && !isFiltersVisible) {
      collection.setCQLState({
        filter: formatCQLFilterWithProductNames(
          productNamesResults.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const formattedCQLFilter = formatCQLFilterWithTrishna(
        filterData,
        context,
        collection.allProductLevel,
        collection.allProductType
      );

      collection.setCQLState({ filter: formattedCQLFilter, isDisplayed: true });
    } else {
      collection.setCQLState({ filter: '', isDisplayed: false });
    }
  };

  const handleSetCQLFilterForCO3D = (
    searchedFeaturesSummary: { name: string; collection: string }[],
    collection: {
      allProductLevel?: string[];
      allProductType?: string[];
      allSatellitePlatform?: string[];
      setCQLState: React.Dispatch<React.SetStateAction<CQLFilterType>>;
    }
  ) => {
    const productNamesResults = searchedFeaturesSummary.filter(
      (feature: { name: string; collection: string }) =>
        !!refGeoserverLayers
          .find((refLayer) => refLayer.layer === 'co3d:gdh_co3d')
          ?.dataTypes.includes(feature.collection)
    );

    const isLayerShown = showLayer('co3d:gdh_co3d');

    if (isLayerShown && productNamesResults.length && !isFiltersVisible) {
      collection.setCQLState({
        filter: formatCQLFilterWithProductNames(
          productNamesResults.map((feature) => feature.name)
        ),
        isDisplayed: true,
      });
    } else if (
      isLayerShown &&
      isFiltersVisible &&
      context.featuresCount > 0 &&
      context.featuresCount < maxFeatures
    ) {
      const formattedCQLFilter = formatCQLFilterWithCO3D(
        filterData,
        context,
        collection.allSatellitePlatform,
        collection.allProductLevel,
      );
      collection.setCQLState({ filter: formattedCQLFilter, isDisplayed: true });
    } else {
      collection.setCQLState({ filter: '', isDisplayed: false });
    }
  };

  const orbitId = filterData[ORBIT_RELATIVE];
  const absoluteOrbitId = filterData[ORBIT_ABSOLUTE];
  const tileIdentifier = filterData[GRID_CODE];

  const handleResetAllCQLFilter = () => {
    setCQLFilterS1({ filter: '', isDisplayed: false });
    setCQLFilterSentinel2({ filter: '', isDisplayed: false });
    setCQLFilterS2L1C({ filter: '', isDisplayed: false });
    setCQLFilterVenus({ filter: '', isDisplayed: false });
    setCQLFilterSpot_reflectance({ filter: '', isDisplayed: false });
    setCQLFilterSpirit({ filter: '', isDisplayed: false });
    setCQLFilterOSO({ filter: '', isDisplayed: false });
    setCQLFilterLandSat57({ filter: '', isDisplayed: false });
    setCQLFilterLandSat8({ filter: '', isDisplayed: false });
    setCQLFilterGdhSnow({ filter: '', isDisplayed: false });
    setCQLFilterPostel({ filter: '', isDisplayed: false });
    setCQLFilterTrishna({ filter: '', isDisplayed: false });
    setCQLFilterCO3D({ filter: '', isDisplayed: false });
  };

  /**
   * Show Tiles and build CQL filters on searchedFeatures change if search < maxFeatures.
   */
  useEffect(() => {
    if (filterData[DATASET]?.length) {
      if (context.mapExtent.extent?.length) {
        setBbox(context.mapExtent.extent.join());
        setTileGrid(
          new TileGrid({
            extent: context.mapExtent.bbox,
            resolutions: resolutions,
          })
        );
      }
      if (context.searchedFeatures) {
        const searchedFeaturesSummary = context.searchedFeatures?.map(
          (feature: Feature) => {
            return {
              name: feature.properties[IDENTIFIER],
              collection: feature.collection,
            };
          }
        );
        handleSetCQLFilterForOSO(searchedFeaturesSummary);
        handleSetCQLFilterForLandsat57(searchedFeaturesSummary);
        handleSetCQLFilterForLandsat8(searchedFeaturesSummary);
        handleSetCQLFilterForMultipleCollections(searchedFeaturesSummary, [
          {
            layer: 'muscate:gdh_venus',
            allSatellitePlatform: ['VM1', 'VM5'],
            allProductLevel: ['L1C', 'L2A', 'L3A'],
            setCQLState: setCQLFilterVenus,
          },
          {
            layer: 'histo:gdh_spot',
            allSatellitePlatform: [
              'SPOT1',
              'SPOT2',
              'SPOT3',
              'SPOT4',
              'SPOT5',
              'SPOT4_TAKE5',
              'SPOT5_TAKE5',
            ],
            allProductLevel: ['L1C', 'L2A', 'L1A'],
            setCQLState: setCQLFilterSpot_reflectance,
          },
        ]);
        handleSetCQLFilterForSpirit(searchedFeaturesSummary, {
          setCQLState: setCQLFilterSpirit,
        });
        handleSetCQLFilterForGdhSnow(searchedFeaturesSummary);
        handleSetCQLFilterForPostel(searchedFeaturesSummary);
        handleSetCQLFilterForS1(searchedFeaturesSummary);
        handleSetCQLFilterForS2L1C(searchedFeaturesSummary);
        handleSetCQLFilterForS2(searchedFeaturesSummary);
        handleSetCQLFilterForTrishna(searchedFeaturesSummary, {
          allProductLevel: ['L1C', 'L2A', 'L2B', 'L3'],
          allProductType: ['DEM', 'IMREF', 'Multispectral'],
          setCQLState: setCQLFilterTrishna,
        });
        handleSetCQLFilterForCO3D(searchedFeaturesSummary, {
          allSatellitePlatform: ['CO3D1', 'CO3D2', 'CO3D3', 'CO3D4', 'CO3DX'],
          allProductLevel: ['L2A', 'L2C', 'L3', 'L4'],
          allProductType: ['REFLECTANCE', 'DEM'],
          setCQLState: setCQLFilterCO3D,
        });
      }
    } else {
      handleResetAllCQLFilter();
    }
    // eslint-disable-next-line
  }, [
    filterData,
    isFiltersVisible,
    orbitId,
    absoluteOrbitId,
    tileIdentifier,
    context.featuresCount,
    context.drawnShape,
    context.searchedFeatures,
    context.mapExtent,
  ]);

  useEffect(() => {
    setMapViewContainerInputs(
      prepareMapViewContainerInputs(changeSourceToOSM, changeSource)
    );
    // eslint-disable-next-line
  }, []);

  /**
   * Show geoserver layer if filterdata contains one of the dataTypes from wanted layer.
   * @param layerName string
   * @returns isLayerShown bool
   */
  const showLayer = (layerName: string) => {
    // check if the layer has dataset in the filterData.
    const showLayerByDataset = !!refGeoserverLayers.find(
      (refLayer: { dataTypes: string[]; layer: string }) =>
        refLayer.layer === layerName &&
        !!refLayer.dataTypes.find((refDataType) =>
          filterData[DATASET]?.includes(refDataType)
        )
    );

    // if no platform is selected, show layer. Otherwise, check if the layer has platform in the filterData.
    const showLayerByPlatforms =
      Array.isArray(filterData?.[PLATFORM]) && filterData[PLATFORM].length > 0
        ? !!refGeoserverLayers
            ?.find((l) => l.layer === layerName)
            ?.platforms?.some((p) =>
              (filterData[PLATFORM] as string[]).includes(p)
            )
        : true;

    // if no dataProduct is selected, show layer. Otherwise, check if the layer has dataProduct in the filterData.
    const showLayerByDataProduct =
      !!filterData['dataProduct'] && filterData['dataProduct']?.length !== 0
        ? !!refGeoserverLayers
            .find((refLayer) => refLayer.layer === layerName)
            ?.dataproducts.some((dataproductFromCollection) =>
              filterData['dataProduct']?.includes(dataproductFromCollection)
            )
        : true;

    // if no sensorMode is selected, show layer. Otherwise, check if the layer has dataProduct in the filterData.
    const showLayerBySensorMode =
      !!filterData[INSTRUMENT_MODE] && filterData[INSTRUMENT_MODE]?.length !== 0
        ? !!refGeoserverLayers
            .find((refLayer) => refLayer.layer === layerName)
            ?.instruments.some((instrumentFromCollection) =>
              filterData[INSTRUMENT_MODE]?.includes(instrumentFromCollection)
            )
        : true;

    // if no visibility is selected, show layer. Otherwise, check if the layer has visibility in the filterData.
    const showLayerByVisibility =
      !!filterData[VISIBILITY] && filterData[VISIBILITY]?.length !== 0
        ? refGeoserverLayers.find((refLayer) => refLayer.layer === layerName)
            ?.visibility?.length !== 0
        : true;

    // if no acquisitionType is selected, show layer. Otherwise, check if the layer has acquisitionType in the filterData.
    const showLayerByAcquisitionType =
      !!filterData[PRODUCT_ACQUISITION_TYPE] &&
      filterData[PRODUCT_ACQUISITION_TYPE]?.length !== 0
        ? refGeoserverLayers.find((refLayer) => refLayer.layer === layerName)
            ?.acquisitiontype?.length !== 0
        : true;

    const showLayerByCloudCover =
      !!filterData[CLOUD_COVER] &&
      filterData[CLOUD_COVER]?.length !== 0 &&
      !(
        Array.isArray(filterData[CLOUD_COVER]) &&
        filterData[CLOUD_COVER].length === 2 &&
        filterData[CLOUD_COVER][0] === '0' &&
        filterData[CLOUD_COVER][1] === '100'
      )
        ? tileSourceReference.find((refLayer) => refLayer.LAYERS === layerName)
            ?.hasCloudCover
        : true;

    const showLayerByWaterCover =
      !!filterData[WATER_COVER] &&
      filterData[WATER_COVER]?.length !== 0 &&
      !(
        Array.isArray(filterData[WATER_COVER]) &&
        filterData[WATER_COVER].length === 2 &&
        filterData[WATER_COVER][0] === '0' &&
        filterData[WATER_COVER][1] === '100'
      )
        ? tileSourceReference.find((refLayer) => refLayer.LAYERS === layerName)
            ?.hasWaterCover
        : true;

    const showLayerBySnowCover =
      !!filterData[EO_SNOW_COVER] &&
      filterData[EO_SNOW_COVER]?.length !== 0 &&
      !(
        Array.isArray(filterData[EO_SNOW_COVER]) &&
        filterData[EO_SNOW_COVER].length === 2 &&
        filterData[EO_SNOW_COVER][0] === '0' &&
        filterData[EO_SNOW_COVER][1] === '100'
      )
        ? tileSourceReference.find((refLayer) => refLayer.LAYERS === layerName)
            ?.hasSnowCover
        : true;

    const showLayerByNoDataPixel =
      !!filterData[NODATA_PIXEL] &&
      filterData[NODATA_PIXEL]?.length !== 0 &&
      !(
        Array.isArray(filterData[NODATA_PIXEL]) &&
        filterData[NODATA_PIXEL].length === 2 &&
        filterData[NODATA_PIXEL][0] === '0' &&
        filterData[NODATA_PIXEL][1] === '100'
      )
        ? tileSourceReference.find((refLayer) => refLayer.LAYERS === layerName)
            ?.hasNoDataPixel
        : true;

    // return true if all conditions are met to show the layer.
    return (
      showLayerByDataset &&
      showLayerByPlatforms &&
      showLayerByDataProduct &&
      showLayerBySensorMode &&
      showLayerByVisibility &&
      showLayerByAcquisitionType &&
      showLayerByCloudCover &&
      showLayerByWaterCover &&
      showLayerBySnowCover &&
      showLayerByNoDataPixel
    );
  };

  return (
    <div className="mapContainer" aria-label="mapContainer">
      <Map
        center={fromLonLat(center)}
        zoom={zoom}
        isInDrawMode={isInDrawMode}
        setIsInDrawMode={setIsInDrawMode}
        tileSourceTable={tileSourceTable}
        time={() =>
          formatTimeParameter(
            isFiltersVisible,
            filterData,
            context.searchedFeatures
          )
        }
        isFiltersVisible={isFiltersVisible}
      >
        <Layers>
          <TileLayer source={source} zIndex={0} />
          {process.env.NODE_ENV === 'production' && (
            <>
              {!!tileSourceTable.length &&
                tileSourceTable.map((tileSource) => {
                  return (
                    tileSource.showLayer && (
                      <TileLayer
                        key={tileSource.tile.getKey()}
                        source={tileSource.tile}
                        zIndex={0}
                      />
                    )
                  );
                })}
            </>
          )}
          {/* LAYER USING REGARDS as SOURCE FOR selected feature from results */}
          {!isFiltersVisible && selectedFeature && (
            <VectorLayer
              key={selectedFeature.id}
              source={vector({
                features: new GeoJSON().readFeatures(selectedFeature, {
                  featureProjection: get('EPSG:3857') as Projection,
                }),
              })}
              style={styles.SinglePolygon}
              zIndex={0}
            />
          )}
          {/* LAYER USING REGARDS as SOURCE FOR highlight on map */}
          {!isFiltersVisible &&
            context.searchedFeatures?.map((feature) => {
              return (
                <VectorLayer
                  key={feature.id}
                  source={vector({
                    features: new GeoJSON().readFeatures(feature, {
                      featureProjection: get('EPSG:3857') as Projection,
                    }),
                  })}
                  style={styles.MultiPolygon}
                  zIndex={0}
                />
              );
            })}
        </Layers>
        <Controls>
          <FullScreenControl />
          <ZoomControl />
          {isFiltersVisible && (
            <DrawShapeControl
              isDrawModeOn={isInDrawMode}
              setDrawMode={setIsInDrawMode}
            />
          )}
          <ChangeTileControl setChangeTileMode={setChangeTileMode} />
        </Controls>
      </Map>
      <Dialog
        header="Select map background"
        visible={isInChangeTileMode}
        onHide={() => setChangeTileMode(false)}
      >
        <div className="mapView">
          {mapViewContainerInputs?.map((inputs) => {
            return (
              <MapViewContainer
                provider={inputs.provider}
                label={inputs.label}
                links={inputs.links}
                clickFunction={inputs.clickFunction}
                key={inputs.label}
              ></MapViewContainer>
            );
          })}
        </div>
      </Dialog>
    </div>
  );
};
export default MapContainer;
